/**
 * RemindersPage — Full follow-up reminders view.
 * Tabs: Active / Snoozed view with filter by reason.
 */

import { useState, useEffect, useCallback } from 'react';
import { Bell, Clock, X, MessageSquare, RefreshCw, Filter } from 'lucide-react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import api from '../services/api';

interface FollowUpSuggestion {
  contact_id: string;
  name: string;
  company: string | null;
  title: string | null;
  warmth: number;
  reason: string;
  urgency: 'high' | 'medium' | 'low';
  last_interaction: string | null;
  days_since_interaction: number | null;
  suggested_action: string;
  message_template: string | null;
  contact_url: string | null;
}

interface ReminderStats {
  cooling_count: number;
  untouched_high_value: number;
  recent_uncontacted: number;
  total_pending: number;
}

const REASON_LABELS: Record<string, string> = {
  cooling_off:          'Cooling Off',
  high_value_untouched: 'High Value — Never Contacted',
  recently_connected:   'Recently Connected',
  pipeline_company:     'Pipeline Company',
  meeting_no_followup:  'Meeting — No Follow-up',
  unanswered_email:     'Unanswered Email',
};

const ALL_REASONS = Object.keys(REASON_LABELS);

const URGENCY_COLORS: Record<string, string> = {
  high:   '#ef4444',
  medium: '#f59e0b',
  low:    '#3b82f6',
};

function warmthColor(w: number): string {
  if (w > 60) return '#ef4444';
  if (w > 40) return '#f59e0b';
  if (w > 20) return '#22c55e';
  return '#6b7280';
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

export default function RemindersPage() {
  const [suggestions, setSuggestions] = useState<FollowUpSuggestion[]>([]);
  const [stats, setStats] = useState<ReminderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [snoozedIds, setSnoozedIds] = useState<Set<string>>(new Set());
  const [copyingId, setCopyingId] = useState<string | null>(null);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [suggestRes, statsRes] = await Promise.allSettled([
        api.get('/reminders/follow-ups', { params: { limit: 50 } }),
        api.get('/reminders/stats'),
      ]);
      if (suggestRes.status === 'fulfilled') {
        setSuggestions(suggestRes.value.data || []);
      }
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      }
    } catch {
      // degrade silently
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSnooze = async (contactId: string) => {
    try {
      await api.post(`/reminders/snooze/${contactId}`, { days: 7 });
      setSnoozedIds(prev => new Set([...prev, contactId]));
    } catch {
      // best-effort
    }
  };

  const handleDismiss = async (contactId: string) => {
    try {
      await api.post(`/reminders/dismiss/${contactId}`);
      setDismissedIds(prev => new Set([...prev, contactId]));
    } catch {
      // best-effort
    }
  };

  const handleCopyTemplate = async (suggestion: FollowUpSuggestion) => {
    if (!suggestion.message_template) return;
    try {
      await navigator.clipboard.writeText(suggestion.message_template);
      setCopyingId(suggestion.contact_id);
      setTimeout(() => setCopyingId(null), 1500);
    } catch {
      // clipboard access denied
    }
  };

  const handleBulkSnooze = async () => {
    const toSnooze = filtered.map(s => s.contact_id);
    await Promise.allSettled(
      toSnooze.map(id => api.post(`/reminders/snooze/${id}`, { days: 7 }))
    );
    setSnoozedIds(prev => new Set([...prev, ...toSnooze]));
  };

  // Bulk dismiss available for future UI button
  // const handleBulkDismiss = async () => {
  //   const toDismiss = filtered.map(s => s.contact_id);
  //   await Promise.allSettled(toDismiss.map(id => api.post(`/reminders/dismiss/${id}`)));
  //   setDismissedIds(prev => new Set([...prev, ...toDismiss]));
  // };

  const visible = suggestions.filter(
    s => !dismissedIds.has(s.contact_id) && !snoozedIds.has(s.contact_id)
  );
  const filtered = activeFilter === 'all'
    ? visible
    : visible.filter(s => s.reason === activeFilter);

  // Count per reason for filter pills
  const reasonCounts = visible.reduce<Record<string, number>>((acc, s) => {
    acc[s.reason] = (acc[s.reason] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="page-enter">
      <Header
        title="Follow-up Reminders"
        subtitle={`${stats?.total_pending ?? 0} contacts need your attention`}
      />

      <div className="p-4 md:p-8 space-y-6">

        {/* Stats row */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Pending',   value: stats.total_pending,       color: '#ef4444' },
              { label: 'Cooling Off',      value: stats.cooling_count,       color: '#f59e0b' },
              { label: 'High Value Untouched', value: stats.untouched_high_value, color: '#a855f7' },
              { label: 'Recent Uncontacted',   value: stats.recent_uncontacted,   color: '#22c55e' },
            ].map(card => (
              <div
                key={card.label}
                className="rounded-xl p-4"
                style={{
                  background: `${card.color}10`,
                  border: `1px solid ${card.color}25`,
                }}
              >
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                  {card.label}
                </p>
                <span className="font-display text-2xl" style={{ color: card.color }}>
                  {card.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Filter pills + actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <Filter size={14} style={{ color: 'var(--text-muted)' }} />
          {['all', ...ALL_REASONS].map(reason => {
            const count = reason === 'all' ? visible.length : (reasonCounts[reason] || 0);
            if (count === 0 && reason !== 'all') return null;
            return (
              <button
                key={reason}
                onClick={() => setActiveFilter(reason)}
                className="text-xs px-3 py-1.5 rounded-full transition-all"
                style={{
                  background: activeFilter === reason
                    ? 'var(--netrun-green)'
                    : 'rgba(255,255,255,0.05)',
                  color: activeFilter === reason ? '#000' : 'var(--text-secondary)',
                  border: `1px solid ${activeFilter === reason ? 'var(--netrun-green)' : 'var(--border-primary)'}`,
                  cursor: 'pointer',
                }}
              >
                {reason === 'all' ? 'All' : REASON_LABELS[reason] || reason}
                {count > 0 && <span className="ml-1.5 opacity-70">({count})</span>}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleBulkSnooze}
              disabled={filtered.length === 0}
              className="text-xs px-3 py-1.5 rounded-lg"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-secondary)',
                cursor: filtered.length > 0 ? 'pointer' : 'not-allowed',
                opacity: filtered.length > 0 ? 1 : 0.5,
              }}
            >
              Snooze all 7d
            </button>
            <button
              onClick={() => loadData(true)}
              className="p-1.5 rounded-lg"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
              title="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Reminders list */}
        <Panel noPadding>
          {loading && (
            <div className="px-5 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              Loading follow-ups...
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-5 py-12 text-center">
              <Bell size={32} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {activeFilter === 'all'
                  ? "You're all caught up — no follow-ups needed."
                  : `No ${REASON_LABELS[activeFilter] || activeFilter} reminders right now.`}
              </p>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
              {filtered.map(s => (
                <div key={s.contact_id} className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    {/* Urgency dot */}
                    <div
                      className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                      style={{ background: URGENCY_COLORS[s.urgency] }}
                    />
                    <div className="flex-1 min-w-0">
                      {/* Name + badges */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {s.name}
                        </span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                          style={{
                            background: `${warmthColor(s.warmth)}15`,
                            color: warmthColor(s.warmth),
                          }}
                        >
                          warmth {s.warmth}
                        </span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded"
                          style={{
                            background: `${URGENCY_COLORS[s.urgency]}12`,
                            color: URGENCY_COLORS[s.urgency],
                          }}
                        >
                          {REASON_LABELS[s.reason] || s.reason}
                        </span>
                      </div>

                      {/* Company / title */}
                      {(s.title || s.company) && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                          {[s.title, s.company].filter(Boolean).join(' · ')}
                        </p>
                      )}

                      {/* Suggested action */}
                      <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                        {s.suggested_action}
                      </p>

                      {/* Message template preview */}
                      {s.message_template && (
                        <p
                          className="text-xs mt-1.5 p-2.5 rounded-lg italic"
                          style={{
                            background: 'rgba(144,185,171,0.06)',
                            border: '1px solid rgba(144,185,171,0.1)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          "{s.message_template.slice(0, 120)}{s.message_template.length > 120 ? '…' : ''}"
                        </p>
                      )}

                      {/* Last interaction */}
                      {s.days_since_interaction !== null && (
                        <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                          Last interaction: {s.last_interaction ? relativeTime(s.last_interaction) : 'never'}
                          {s.days_since_interaction !== null && ` (${s.days_since_interaction}d ago)`}
                        </p>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-2.5">
                        {s.contact_url && (
                          <a
                            href={s.contact_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-all"
                            style={{
                              background: 'rgba(144,185,171,0.1)',
                              border: '1px solid rgba(144,185,171,0.2)',
                              color: 'var(--netrun-green)',
                              textDecoration: 'none',
                            }}
                          >
                            <MessageSquare size={11} />
                            Open LinkedIn
                          </a>
                        )}
                        {s.message_template && (
                          <button
                            onClick={() => handleCopyTemplate(s)}
                            className="text-xs px-2.5 py-1.5 rounded-lg transition-all"
                            style={{
                              background: copyingId === s.contact_id
                                ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.04)',
                              border: '1px solid var(--border-primary)',
                              color: copyingId === s.contact_id
                                ? '#22c55e' : 'var(--text-secondary)',
                              cursor: 'pointer',
                            }}
                          >
                            {copyingId === s.contact_id ? 'Copied!' : 'Copy message'}
                          </button>
                        )}
                        <button
                          onClick={() => handleSnooze(s.contact_id)}
                          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg"
                          style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid var(--border-primary)',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                          }}
                        >
                          <Clock size={11} />
                          Snooze 7d
                        </button>
                        <button
                          onClick={() => handleDismiss(s.contact_id)}
                          className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                          }}
                          title="Dismiss permanently"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
